/**
 * Internationalisation setup.
 *
 * Two decisions here are deliberate and easy to undo by accident:
 *
 *   1. **Interpolation escaping is off.** React escapes everything it renders,
 *      so i18next escaping again turns an apostrophe into `&#39;` on screen.
 *      This is safe ONLY because values reach the DOM through React — never
 *      concatenate a translated string into `dangerouslySetInnerHTML`.
 *   2. **Number formatting does not follow the UI language.** PRD §9.5 requires
 *      comma thousands and full-stop decimals in every language, so `lib/format`
 *      pins its own locale. i18next's `formatNumber` is deliberately not used.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.12, §7.4
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';

/** Languages the interface is offered in. */
export const SUPPORTED_LANGUAGES = ['en', 'pt', 'es'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/** Where the chosen language is remembered between visits. */
export const LANGUAGE_STORAGE_KEY = 'nexus.language';

/** Narrows an arbitrary string to a language we actually ship. */
export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Resolves the starting language.
 *
 * A stored choice wins over the browser's preference — someone who switched to
 * English on a Portuguese machine meant it. Only the primary subtag is
 * considered, so `pt-BR` resolves to `pt`.
 */
export function resolveInitialLanguage(
  stored: string | null,
  browserLanguages: readonly string[] = [],
): SupportedLanguage {
  if (stored !== null && isSupportedLanguage(stored)) {
    return stored;
  }

  for (const candidate of browserLanguages) {
    /* `split` always yields at least one element, so destructuring the head
       cannot produce undefined for a string input. */
    const [head = ''] = candidate.split('-');
    const primary = head.toLowerCase();
    if (isSupportedLanguage(primary)) {
      return primary;
    }
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Reads the stored language.
 *
 * Wrapped because Safari in private mode throws on `localStorage` access
 * rather than returning null, and a language preference is not worth crashing
 * the application over. The try/catch is the guard — the property itself is
 * always defined, so an optional chain would protect against nothing.
 */
export function readStoredLanguage(): string | null {
  try {
    return globalThis.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persists the language, ignoring storage failures for the same reason. */
export function storeLanguage(language: SupportedLanguage): void {
  try {
    globalThis.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    /* Preference is lost on reload; the application still works. */
  }
}

/**
 * Keeps `<html lang>` in step with the active language.
 *
 * Screen readers choose a pronunciation from this attribute, so a stale value
 * makes the page read aloud in the wrong accent.
 */
export function applyDocumentLanguage(language: SupportedLanguage): void {
  document.documentElement.lang = language;
}

export const defaultNamespace = 'common';

/** Initialises i18next. Idempotent, so tests may call it freely. */
export async function initI18n(
  language: SupportedLanguage = DEFAULT_LANGUAGE,
): Promise<typeof i18n> {
  if (i18n.isInitialized) {
    await i18n.changeLanguage(language);
    return i18n;
  }

  await i18n.use(initReactI18next).init({
    lng: language,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: defaultNamespace,
    ns: [defaultNamespace],
    resources: {
      en: { common: enCommon },
    },
    interpolation: {
      /* See the note at the top of this file. */
      escapeValue: false,
    },
    /* A missing key should be obvious in development and harmless in
       production, where the fallback language fills the gap. */
    returnEmptyString: false,
  });

  return i18n;
}

export { i18n };
